import React, { useState, useEffect } from 'react';
import { Button, Form, Modal, Card, Badge } from 'react-bootstrap';
import { NewsletterTemplate } from '../newsletter.types';
import {
  getNewsletterTemplates,
  createNewsletterTemplate,
  updateNewsletterTemplate,
  deleteNewsletterTemplate,
} from 'helpers/http/common';
import toast from 'react-hot-toast';
import { Trash2, Edit, Save, X } from 'lucide-react';
import moment from 'moment';

interface TemplateManagerProps {
  onSelectTemplate: (subject: string, htmlContent: string) => void;
}

const TemplateManager: React.FC<TemplateManagerProps> = ({
  onSelectTemplate,
}) => {
  const [templates, setTemplates] = useState<NewsletterTemplate[]>([]);
  const [showModal, setShowModal] = useState(false);
  const [editingTemplate, setEditingTemplate] =
    useState<NewsletterTemplate | null>(null);
  const [formData, setFormData] = useState({
    name: '',
    subject: '',
    htmlContent: '',
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchTemplates();
  }, []);

  const fetchTemplates = async () => {
    try {
      setLoading(true);
      const data = await getNewsletterTemplates();
      setTemplates(data.templates || []);
    } catch (error) {
      console.error('Error fetching templates:', error);
      toast.error('Failed to load templates');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveTemplate = async () => {
    if (!formData.name.trim()) {
      toast.error('Please enter a template name');
      return;
    }
    if (!formData.subject.trim()) {
      toast.error('Please enter a subject');
      return;
    }
    if (!formData.htmlContent.trim()) {
      toast.error('Please enter content');
      return;
    }

    try {
      setLoading(true);
      if (editingTemplate) {
        await updateNewsletterTemplate(editingTemplate.template_id, formData);
        toast.success('Template updated successfully!');
      } else {
        await createNewsletterTemplate(formData);
        toast.success('Template created successfully!');
      }
      fetchTemplates();
      handleCloseModal();
    } catch (error: any) {
      toast.error(
        error?.message ||
          `Failed to ${editingTemplate ? 'update' : 'create'} template`
      );
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteTemplate = async (templateId: number, name: string) => {
    const confirmed = window.confirm(
      `Are you sure you want to delete "${name}" template?`
    );
    if (!confirmed) return;

    try {
      await deleteNewsletterTemplate(templateId);
      toast.success('Template deleted successfully!');
      fetchTemplates();
    } catch (error: any) {
      toast.error(error?.message || 'Failed to delete template');
    }
  };

  const handleEditTemplate = (template: NewsletterTemplate) => {
    setEditingTemplate(template);
    setFormData({
      name: template.name,
      subject: template.subject,
      htmlContent: template.htmlContent,
    });
    setShowModal(true);
  };

  const handleUseTemplate = (template: NewsletterTemplate) => {
    onSelectTemplate(template.subject, template.htmlContent);
    toast.success(`Template "${template.name}" loaded!`);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingTemplate(null);
    setFormData({ name: '', subject: '', htmlContent: '' });
  };

  const handleSaveCurrentAsTemplate = (
    currentSubject: string,
    currentContent: string
  ) => {
    setFormData({
      name: '',
      subject: currentSubject,
      htmlContent: currentContent,
    });
    setShowModal(true);
  };

  return (
    <div className="template-manager">
      <div className="d-flex align-items-center justify-content-between mb-3">
        <h6 className="mb-0 fw-600">Email Templates</h6>
        <Button
          variant="outline-primary"
          size="sm"
          onClick={() => setShowModal(true)}
        >
          <Save size={16} className="me-1" />
          Save New Template
        </Button>
      </div>

      {loading && templates.length === 0 ? (
        <div className="text-center py-3 text-muted">Loading templates...</div>
      ) : templates.length === 0 ? (
        <div className="text-center py-3 text-muted">
          No templates saved yet. Create your first template!
        </div>
      ) : (
        <div className="templates-list">
          {templates.map((template) => (
            <Card key={template.template_id} className="mb-2 template-card">
              <Card.Body className="p-3">
                <div className="d-flex justify-content-between align-items-start">
                  <div className="flex-grow-1">
                    <div className="d-flex align-items-center mb-1">
                      <strong>{template.name}</strong>
                      <Badge bg="light" text="dark" className="ms-2 small">
                        {moment(template.created_at).format('MMM DD, YYYY')}
                      </Badge>
                    </div>
                    <div className="text-muted small mb-2">
                      {template.subject}
                    </div>
                  </div>
                  <div className="d-flex gap-1">
                    <Button
                      variant="outline-success"
                      size="sm"
                      onClick={() => handleUseTemplate(template)}
                      title="Use this template"
                    >
                      Use
                    </Button>
                    <Button
                      variant="outline-secondary"
                      size="sm"
                      onClick={() => handleEditTemplate(template)}
                      title="Edit template"
                    >
                      <Edit size={14} />
                    </Button>
                    <Button
                      variant="outline-danger"
                      size="sm"
                      onClick={() =>
                        handleDeleteTemplate(
                          template.template_id,
                          template.name
                        )
                      }
                      title="Delete template"
                    >
                      <Trash2 size={14} />
                    </Button>
                  </div>
                </div>
              </Card.Body>
            </Card>
          ))}
        </div>
      )}

      {/* Template Modal */}
      <Modal show={showModal} onHide={handleCloseModal} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>
            {editingTemplate ? 'Edit Template' : 'Save as Template'}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="alert alert-info mb-3">
            <strong>💡 Personalization Tip:</strong> Use{' '}
            <code>{`{{first_name}}`}</code> and <code>{`{{last_name}}`}</code>{' '}
            in your subject and content. They'll be automatically replaced with
            each recipient's name when sending. Example:{' '}
            <em>"Hello {`{{first_name}}`}!"</em> becomes <em>"Hello John!"</em>
          </div>
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>Template Name *</Form.Label>
              <Form.Control
                type="text"
                placeholder="e.g., Welcome Email, Monthly Newsletter"
                value={formData.name}
                onChange={(e) =>
                  setFormData({ ...formData, name: e.target.value })
                }
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Email Subject *</Form.Label>
              <Form.Control
                type="text"
                placeholder="e.g., Welcome {{first_name}}!"
                value={formData.subject}
                onChange={(e) =>
                  setFormData({ ...formData, subject: e.target.value })
                }
              />
              <Form.Text className="text-muted">
                Use <code>{`{{first_name}}`}</code> and{' '}
                <code>{`{{last_name}}`}</code> for personalization
              </Form.Text>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>HTML Content *</Form.Label>
              <Form.Control
                as="textarea"
                rows={10}
                placeholder="<h1>Hello {{first_name}}!</h1><p>Welcome to our platform...</p>"
                value={formData.htmlContent}
                onChange={(e) =>
                  setFormData({ ...formData, htmlContent: e.target.value })
                }
                style={{ fontFamily: 'monospace', fontSize: '0.9rem' }}
              />
              <Form.Text className="text-muted">
                HTML code with optional personalization placeholders
              </Form.Text>
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCloseModal}>
            <X size={16} className="me-1" />
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleSaveTemplate}
            disabled={loading}
          >
            <Save size={16} className="me-1" />
            {loading
              ? 'Saving...'
              : editingTemplate
              ? 'Update Template'
              : 'Save Template'}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default TemplateManager;
